// v2.6 — pure helpers for the approver "By person" table.
//
// Excluded users (logging_exempt = true on the server, surfaced as
// `loggingExempt` on each row) are not required to log time, so they must
// never move a stat: they're partitioned out before any count, filter, or
// bulk-approve set is computed. They're still rendered — in their own
// "Excluded · not required to log" group — but always derived from the
// `exempt` half, never the `tracked` half.
//
// Kept here as plain functions (no React) so the on-track filter + the
// exempt exclusion are unit-tested directly, the way the rest of the math
// lives in lib/ (format.js, clientMatch.js).

// Filter pills in display order. "On track" (status === 'ok') is the v2.6
// addition; the order is All · On track · Warning + Alert · Pending.
export const TEAM_FILTERS = [
  { k: 'all', label: 'All' },
  { k: 'ontrack', label: 'On track' },
  { k: 'risky', label: 'Warning + Alert' },
  { k: 'pending', label: 'Pending' },
];

const isApproved = (r) => r.approval?.state === 'approved';

/**
 * Split rows into { tracked, exempt } by the loggingExempt flag. Order
 * within each half is preserved.
 */
export function partitionExempt(rows = []) {
  const tracked = [];
  const exempt = [];
  for (const r of rows) {
    if (r.loggingExempt) exempt.push(r);
    else tracked.push(r);
  }
  return { tracked, exempt };
}

/**
 * Filter pill counts — computed over TRACKED rows only, so an excluded
 * user never appears in any denominator. Pass the tracked half.
 */
export function teamCounts(tracked = []) {
  return {
    all: tracked.length,
    ontrack: tracked.filter((r) => r.status === 'ok').length,
    risky: tracked.filter((r) => r.status !== 'ok').length,
    pending: tracked.filter((r) => !isApproved(r)).length,
  };
}

/**
 * Apply a filter pill to the tracked rows. "On track" → status === 'ok';
 * "Warning + Alert" → status !== 'ok'; "Pending" → not yet approved;
 * "All" (or anything unknown) → every tracked row. Excluded users are
 * never part of `tracked`, so they never match a status filter.
 */
export function applyTeamFilter(tracked = [], filter = 'all') {
  if (filter === 'ontrack') return tracked.filter((r) => r.status === 'ok');
  if (filter === 'risky') return tracked.filter((r) => r.status !== 'ok');
  if (filter === 'pending') return tracked.filter((r) => !isApproved(r));
  return tracked;
}
